/**
 * Auto-Create Quotation from Classified Email
 *
 * Used when "Auto Create" mode is enabled in Gmail Settings.
 * Runs buyer/item matching automatically and creates the quotation
 * without any manual review step.
 *
 * Match resolution rules:
 *   - Exact match  → use existing buyer/item
 *   - Fuzzy match  → use the best candidate automatically
 *   - No match     → create new buyer/item master with extracted data
 */

import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/utils/axiosInterceptor";
import { useToast } from "@/hooks/use-toast";
import { useBuyers } from "@/hooks/useBuyers";
import { useItems } from "@/hooks/useItems";
import { useConvertEmailToQuotation } from "@/hooks/useGmailEmails";
import {
  matchBuyer,
  matchAllLineItems,
  type BuyerRecord,
  type ItemRecord,
} from "@/utils/matchingEngine";
import type { ClassifyEmailResult, ExtractedData } from "@/types/gmail";
import { normalizeExtractedData } from "@/utils/normalizeExtractedData";
import { resolveUomCode } from "@/lib/uom";

// ==================== Types ====================

interface AutoCreateResult {
  emailMessageId: string;
  success: boolean;
  quotationId?: string;
  quotationCode?: string;
  error?: string;
}

// ==================== Defaults for missing buyer fields ====================

const BUYER_DEFAULTS = {
  tin: "C000000000",
  registrationType: "BRN",
  registrationNumber: "0000000000",
  name: "John Doe",
  email: "johndoe@mail.com",
  contactNumber: "0100000000",
  addressLine0: "Address Line 1",
  city: "City",
  state: "17", // Not Applicable
  country: "MYS",
};

/** Generate the next buyer code in BUYER001, BUYER002, ... sequence. */
function generateNextBuyerCode(
  existingBuyers: Array<{ code: string | null }>,
): string {
  const nums = existingBuyers
    .map((b) => b.code)
    .filter((c): c is string => !!c && /^BUYER\d+$/.test(c))
    .map((c) => parseInt(c.replace("BUYER", ""), 10));
  const next = nums.length > 0 ? Math.max(...nums) + 1 : 1;
  return `BUYER${String(next).padStart(3, "0")}`;
}

/** Sanitise a contact number — strips all non-digit chars except a leading +.
 *  Falls back to the default if the result doesn't meet validation requirements. */
function sanitiseContact(raw: string | null | undefined): string {
  if (!raw) return BUYER_DEFAULTS.contactNumber;
  const stripped = raw.replace(/[^\d+]/g, "").replace(/(?!^)\+/g, "");
  return stripped.length >= 8 ? stripped : BUYER_DEFAULTS.contactNumber;
}

/**
 * Sanitise a buyer email.
 * - Extracts address from "Name <email@domain.com>" format
 * - Returns null (rather than an invalid string) if email fails basic validation
 * - "NA" is preserved as-is (backend accepts it as a sentinel value)
 */
function sanitiseBuyerEmail(raw: string | null | undefined): string | null {
  if (!raw) return null;
  // Extract address from angle-bracket format: "Name <email@domain.com>"
  const angleMatch = raw.match(/<([^>]+)>/);
  const candidate = angleMatch ? angleMatch[1].trim() : raw.trim();
  if (!candidate) return null;
  if (candidate === "NA") return "NA";
  // Basic email validation: must contain @ with content on both sides and a dot after @
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate)) return candidate;
  // Invalid — return null so the backend's nullable/optional email field accepts it
  return null;
}

// ==================== Helper: build raw buyer from extracted data ====================

function buildBuyerFromExtracted(extracted: ExtractedData["buyer"]) {
  return {
    code: "" as string, // will be set to autoCode before use
    name:
      extracted.buyer_company_name ||
      extracted.buyer_name ||
      BUYER_DEFAULTS.name,
    tin: BUYER_DEFAULTS.tin,
    email: sanitiseBuyerEmail(extracted.buyer_email) || BUYER_DEFAULTS.email,
    contactNumber: sanitiseContact(extracted.buyer_contact),
    registrationType: BUYER_DEFAULTS.registrationType,
    registrationNumber: BUYER_DEFAULTS.registrationNumber,
    sstRegistrationNumber: null as string | null,
    buyerAddress: {
      addressLine0:
        extracted.buyer_address_line0 || BUYER_DEFAULTS.addressLine0,
      addressLine1: null as string | null,
      addressLine2: null as string | null,
      postalZone: null as string | null,
      cityName: BUYER_DEFAULTS.city,
      state: BUYER_DEFAULTS.state,
      country: BUYER_DEFAULTS.country,
    },
  };
}

// ==================== Hook ====================

export const useAutoCreateQuotation = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { buyers, createBuyerAsync } = useBuyers();
  const { items, createItemAsync } = useItems();
  const convertEmail = useConvertEmailToQuotation();

  const rawBuyers =
    ((buyers?.data ?? buyers) as unknown as Array<{
      id: string;
      code: string | null;
      name: string;
      email: string;
      tin: string;
      registrationType: string;
      registrationNumber: string;
      contactNumber?: string | null;
      buyerAddress?: {
        addressLine0?: string | null;
        addressLine1?: string | null;
        addressLine2?: string | null;
        postalZone?: string | null;
        cityName?: string;
        state?: string;
        country?: string;
      };
    }>) || [];

  const buyerRecords: BuyerRecord[] = rawBuyers.map((b) => ({
    id: b.id,
    buyerCode: b.code,
    buyerName: b.name,
    buyerEmail: b.email,
    buyerTin: b.tin,
    buyerRegistrationType: b.registrationType,
    buyerRegistrationNumber: b.registrationNumber,
    buyerCity: b.buyerAddress?.cityName || "",
    buyerState: b.buyerAddress?.state || "",
    buyerCountry: b.buyerAddress?.country || "",
  }));

  const itemRecords: ItemRecord[] = (
    (items?.data as unknown as Array<{
      id: string;
      itemCode: string;
      description: string;
      classification: string;
      taxType: string;
      taxRate: number;
      unitPrice: number;
      measurement: string | null;
    }>) || []
  ).map((i) => ({
    id: i.id,
    itemCode: i.itemCode,
    description: i.description,
    classification: i.classification,
    taxType: i.taxType,
    taxRate: i.taxRate,
    unitPrice: i.unitPrice,
    measurement: i.measurement,
  }));

  /**
   * Auto-create a quotation for a single classified email result.
   * Returns the created quotation ID and code on success.
   */
  const autoCreate = async (
    classifyResult: ClassifyEmailResult,
    emailId: string, // DB ID of the saved PENDING_REVIEW email record
  ): Promise<AutoCreateResult> => {
    try {
      if (!classifyResult.extractedData) {
        return {
          emailMessageId: classifyResult.messageId,
          success: false,
          error: "No extracted data available for auto-create.",
        };
      }

      const rawExtracted = classifyResult.extractedData as Record<
        string,
        unknown
      >;
      const extractedData = normalizeExtractedData(
        rawExtracted,
      ) as ExtractedData;
      const { buyer: extractedBuyer, line_items: extractedItems = [] } =
        extractedData;

      // --- Fetch quotation prefix ---
      const prefixResponse = await api.get<
        Array<{
          id: string;
          prefix: string;
          lastNumber: string;
          isDefault: boolean;
        }>
      >("/prefix/QT");
      const prefixes = prefixResponse.data || [];
      const selectedPrefix = prefixes.find((p) => p.isDefault) || prefixes[0];
      if (!selectedPrefix) {
        return {
          emailMessageId: classifyResult.messageId,
          success: false,
          error:
            "No quotation prefix configured. Please set up a quotation prefix in settings.",
        };
      }
      // Calculate preview code number (backend will auto-assign the real one)
      const nextNum = parseInt(selectedPrefix.lastNumber, 10) + 1;
      const nextNumStr = String(nextNum).padStart(
        selectedPrefix.lastNumber.length,
        "0",
      );
      const previewCode = `${selectedPrefix.prefix}${nextNumStr}`;

      // --- Resolve buyer ---
      let buyerData: Record<string, unknown>;
      const buyerMatch = matchBuyer(extractedBuyer, buyerRecords);

      if (buyerMatch.type === "exact" || buyerMatch.type === "fuzzy") {
        // Use the best match — pull full record from rawBuyers to get contact/address
        const best = buyerMatch.matches[0];
        const fullBuyer = rawBuyers.find((b) => b.id === best.id);
        buyerData = {
          buyerCode: best.id, // FK to buyers(id) — must be UUID, not the text code
          buyerName: best.buyerName || BUYER_DEFAULTS.name,
          buyerEmail:
            sanitiseBuyerEmail(best.buyerEmail) ??
            sanitiseBuyerEmail(BUYER_DEFAULTS.email),
          buyerRegistrationType:
            best.buyerRegistrationType || BUYER_DEFAULTS.registrationType,
          buyerRegistrationNumber:
            best.buyerRegistrationNumber || BUYER_DEFAULTS.registrationNumber,
          buyerTin: best.buyerTin || BUYER_DEFAULTS.tin,
          buyerCity: best.buyerCity || BUYER_DEFAULTS.city,
          buyerState: best.buyerState || BUYER_DEFAULTS.state,
          buyerCountry: best.buyerCountry || BUYER_DEFAULTS.country,
          buyerContact: sanitiseContact(fullBuyer?.contactNumber),
          buyerAddressLine0:
            fullBuyer?.buyerAddress?.addressLine0 ||
            BUYER_DEFAULTS.addressLine0,
          buyerAddressLine1: fullBuyer?.buyerAddress?.addressLine1 ?? null,
          buyerAddressLine2: fullBuyer?.buyerAddress?.addressLine2 ?? null,
          buyerPostcode: fullBuyer?.buyerAddress?.postalZone ?? null,
          buyerSstRegistrationNumber: null,
        };
      } else {
        // No match — create new buyer master and use extracted data
        const autoCode = generateNextBuyerCode(rawBuyers);
        let newBuyerUUID: string | null = null;
        try {
          const newBuyer = buildBuyerFromExtracted(extractedBuyer);
          newBuyer.code = autoCode;
          const createdBuyerResponse = await createBuyerAsync(newBuyer);
          // createBuyerAsync returns the axios response; .data is the created buyer record
          newBuyerUUID =
            (createdBuyerResponse as { data?: { id?: string } })?.data?.id ??
            null;
          queryClient.invalidateQueries({ queryKey: ["buyers"] });
        } catch {
          // Continue even if buyer master creation fails — quotation will still be created with null buyerCode
        }
        buyerData = {
          buyerCode: newBuyerUUID, // UUID of newly created buyer, or null if creation failed
          buyerName:
            extractedBuyer.buyer_company_name ||
            extractedBuyer.buyer_name ||
            BUYER_DEFAULTS.name,
          buyerEmail:
            sanitiseBuyerEmail(extractedBuyer.buyer_email) ??
            sanitiseBuyerEmail(BUYER_DEFAULTS.email),
          buyerContact: sanitiseContact(extractedBuyer.buyer_contact),
          buyerRegistrationType: BUYER_DEFAULTS.registrationType,
          buyerRegistrationNumber: BUYER_DEFAULTS.registrationNumber,
          buyerTin: BUYER_DEFAULTS.tin,
          buyerCity: BUYER_DEFAULTS.city,
          buyerState: BUYER_DEFAULTS.state,
          buyerCountry: BUYER_DEFAULTS.country,
          buyerAddressLine0:
            extractedBuyer.buyer_address_line0 || BUYER_DEFAULTS.addressLine0,
          buyerAddressLine1: null,
          buyerAddressLine2: null,
          buyerPostcode: null,
          buyerSstRegistrationNumber: null,
        };
      }

      // --- Resolve line items ---
      const itemMatchResults = matchAllLineItems(extractedItems, itemRecords);
      const lineItems = await Promise.all(
        itemMatchResults.map(async ({ extractedItem, matchResult }) => {
          const qty = extractedItem.qty || 1;

          if (matchResult.type === "exact" || matchResult.type === "fuzzy") {
            const best = matchResult.matches[0];
            const unitPrice =
              extractedItem.unit_price_myr || best.unitPrice || 0;
            const taxRate = best.taxRate || 0;
            const subtotal = qty * unitPrice;
            const taxAmount = subtotal * (taxRate / 100);
            // Keep only a published LHDN unit code
            const measurement = resolveUomCode(best.measurement);
            return {
              itemCode: best.itemCode,
              classification: best.classification,
              description: extractedItem.item_desc || best.description,
              unitPrice,
              quantity: qty,
              measurement,
              discountRate: null,
              discountAmount: null,
              feeRate: null,
              feeAmount: null,
              taxType: best.taxType,
              taxRate,
              taxAmount,
              detailsOfTaxExemption: null,
              amountExemptedFromTax: null,
              subtotal,
              totalExcludingTax: subtotal,
              total: subtotal + taxAmount,
              productTariffCode: null,
              countryOfOrigin: null,
            };
          } else {
            // No match — create new item master
            try {
              await createItemAsync({
                itemCode:
                  extractedItem.product_code || `EMAIL-ITEM-${Date.now()}`,
                description: extractedItem.item_desc || "New Item",
                classification: "022",
                unitPrice: extractedItem.unit_price_myr || 0,
                taxType: "06",
                taxRate: 0,
                taxAmount: 0,
                discountRate: 0,
                feeChargeRate: 0,
                countryOfOrigin: "MYS",
                isEditable: 0,
                measurement: "",
                quantity: 0,
                weightedAverage: null,
                salesGlCode: null,
                purchaseGlCode: null,
                stockType: "non-stock",
                detailsOfTaxExemption: "",
                amountExemptedFromTax: 0,
                productTariffCode: "",
                createdAt: new Date(),
              });
              queryClient.invalidateQueries({ queryKey: ["items"] });
            } catch {
              // Continue even if item master creation fails
            }
            const unitPrice = extractedItem.unit_price_myr || 0;
            const subtotal = qty * unitPrice;
            return {
              itemCode: extractedItem.product_code || "NEW-ITEM",
              classification: "022",
              description: extractedItem.item_desc || "",
              unitPrice,
              quantity: qty,
              measurement: null,
              discountRate: null,
              discountAmount: null,
              feeRate: null,
              feeAmount: null,
              taxType: "06",
              taxRate: 0,
              taxAmount: 0,
              detailsOfTaxExemption: null,
              amountExemptedFromTax: null,
              subtotal,
              totalExcludingTax: subtotal,
              total: subtotal,
              productTariffCode: null,
              countryOfOrigin: null,
            };
          }
        }),
      );

      // If extraction returned no items
      if (lineItems.length === 0) {
        lineItems.push({
          itemCode: "NEW-ITEM",
          classification: "022",
          description: "Item (please update)",
          unitPrice: 0,
          quantity: 1,
          measurement: null,
          discountRate: null,
          discountAmount: null,
          feeRate: null,
          feeAmount: null,
          taxType: "06",
          taxRate: 0,
          taxAmount: 0,
          detailsOfTaxExemption: null,
          amountExemptedFromTax: null,
          subtotal: 0,
          totalExcludingTax: 0,
          total: 0,
          productTariffCode: null,
          countryOfOrigin: null,
        });
      }

      // --- Calculate totals from line items ---
      const totalExcludingTax = lineItems.reduce(
        (sum, li) => sum + (li.totalExcludingTax ?? 0),
        0,
      );
      const totalTaxAmount = lineItems.reduce(
        (sum, li) => sum + (li.taxAmount ?? 0),
        0,
      );
      const totalIncludingTax = totalExcludingTax + totalTaxAmount;
      const totalPayableAmount = totalIncludingTax;

      // Group tax amounts by tax type for totalTaxAmountPerTaxType
      const taxByType = lineItems.reduce<Record<string, number>>((acc, li) => {
        const type = li.taxType || "06";
        acc[type] = (acc[type] ?? 0) + (li.taxAmount ?? 0);
        return acc;
      }, {});
      const totalTaxAmountPerTaxType = Object.entries(taxByType).map(
        ([taxType, taxAmount]) => ({
          taxType,
          taxAmount,
        }),
      );

      // --- Create the quotation ---
      const today = new Date();
      const validUntil = new Date(today);
      validUntil.setDate(validUntil.getDate() + 30);

      // Backend quotationSchema uses z.string().datetime() — must be full ISO 8601
      const quotationDateISO = today.toISOString();
      const quotationTimeISO = today.toISOString();

      const quotationPayload = {
        codeNumber: previewCode,
        prefixId: selectedPrefix.id,
        quotationDate: quotationDateISO,
        quotationTime: quotationTimeISO,
        validUntilDate: validUntil.toISOString(),
        currencyCode: "MYR",
        currencyExchangeRate: 0,
        isEmailGenerated: true,
        sourceEmailId: emailId,
        totalExcludingTax,
        totalIncludingTax,
        totalPayableAmount,
        totalTaxAmount,
        totalTaxAmountPerTaxType,
        totalNetAmount: totalExcludingTax,
        totalDiscountValue: 0,
        roundingAmount: 0,
        totalTaxableAmountPerTaxType: totalExcludingTax,
        ...buyerData,
        lineItems,
      };

      const createResponse = await api.post<{
        id: string;
        codeNumber: string;
      }>("/quotations", quotationPayload);
      const createdQuotation = createResponse.data;

      if (!createdQuotation?.id) {
        return {
          emailMessageId: classifyResult.messageId,
          success: false,
          error: "Quotation was created but no ID was returned.",
        };
      }

      // --- Mark email as CONVERTED ---
      await convertEmail.mutateAsync({
        emailId,
        quotationId: createdQuotation.id,
      });

      queryClient.invalidateQueries({ queryKey: ["quotations"] });

      return {
        emailMessageId: classifyResult.messageId,
        success: true,
        quotationId: createdQuotation.id,
        quotationCode: createdQuotation.codeNumber,
      };
    } catch (err) {
      let message = "Unknown error occurred.";
      if (err && typeof err === "object" && "response" in err) {
        const axiosErr = err as {
          response?: { data?: { message?: string; details?: unknown } };
          message?: string;
        };
        const serverData = axiosErr.response?.data;
        if (serverData?.message) {
          message = serverData.message;
          if (serverData.details) {
            console.error("Auto-create validation errors:", serverData.details);
          }
        } else if (axiosErr.message) {
          message = axiosErr.message;
        }
      } else if (err instanceof Error) {
        message = err.message;
      }
      console.error(
        "Auto-create failed for email",
        classifyResult.messageId,
        ":",
        message,
        err,
      );
      return {
        emailMessageId: classifyResult.messageId,
        success: false,
        error: message,
      };
    }
  };

  /**
   * Auto-create quotations for a batch of classified PENDING_REVIEW results.
   * Shows a summary toast when done.
   */
  const autoCreateBatch = async (classifyResults: ClassifyEmailResult[]) => {
    const pendingReview = classifyResults.filter(
      (r) => r.status === "PENDING_REVIEW" && r.savedId && r.extractedData,
    );

    if (pendingReview.length === 0) return;

    const results: AutoCreateResult[] = [];
    for (const r of pendingReview) {
      results.push(await autoCreate(r, r.savedId!));
    }

    const succeeded = results.filter((r) => r.success);
    const failed = results.filter((r) => !r.success);

    if (succeeded.length > 0) {
      toast({
        title: "Quotations Auto-Created",
        description:
          `${succeeded.length} quotation(s) created automatically. ` +
          (failed.length > 0
            ? `${failed.length} failed — check inbox for details.`
            : "View them in the Quotations page."),
        duration: 6000,
      });
    }

    if (failed.length > 0 && succeeded.length === 0) {
      const firstError = failed[0]?.error;
      toast({
        title: "Auto-Create Failed",
        description: firstError
          ? `Could not auto-create ${failed.length} quotation(s): ${firstError}`
          : `Could not auto-create ${failed.length} quotation(s). Check inbox for details.`,
        variant: "destructive",
        duration: 8000,
      });
    }

    return results;
  };

  return { autoCreate, autoCreateBatch };
};
